/**
 * Helper di presentazione per la vista "awareness per reparto".
 *
 * Regola di fondo: A-Sight non calcola nulla. Indice, livello, pesi e soglie
 * arrivano già pronti dalla piattaforma Awareness; qui formattiamo soltanto.
 * L'unica cosa decisa lato A-Sight è il target atteso per reparto.
 */

/** Placeholder per i dati non disponibili: `null` non è mai zero. */
export const DASH = '—';

const isMissing = (value) => value === null || value === undefined || value === '';

/** Percentuale già calcolata a monte: la formattiamo e basta. */
export function fmtPercent(value, digits = 1) {
  if (isMissing(value) || !Number.isFinite(Number(value))) return DASH;
  return `${Number(value).toFixed(digits)}%`;
}

/** Numero intero (conteggi). */
export function fmtNumber(value) {
  if (isMissing(value) || !Number.isFinite(Number(value))) return DASH;
  return String(value);
}

/** Indice di awareness: valore 0-100 già pesato dalla piattaforma. */
export function fmtIndex(value, digits = 1) {
  if (isMissing(value) || !Number.isFinite(Number(value))) return DASH;
  return Number(value).toFixed(digits);
}

/** Scarto rispetto alla media aziendale, con segno esplicito. */
export function fmtDelta(value, digits = 1) {
  if (isMissing(value) || !Number.isFinite(Number(value))) return DASH;
  const num = Number(value);
  const sign = num > 0 ? '+' : '';
  return `${sign}${num.toFixed(digits)}`;
}

/**
 * Soglie dei livelli ordinate dalla più alta alla più bassa.
 * Le chiavi arrivano dal payload (`levels`): non vanno riscritte nel codice.
 */
export function sortedLevels(levels) {
  if (!levels || typeof levels !== 'object') return [];
  return Object.entries(levels)
    .filter(([, threshold]) => Number.isFinite(Number(threshold)))
    .map(([key, threshold]) => ({ key, threshold: Number(threshold) }))
    .sort((a, b) => b.threshold - a.threshold);
}

/**
 * Stile del livello in base al suo rango fra le soglie del payload:
 * il livello più alto è verde, il più basso rosso. Non dipende dai nomi,
 * così resta valido se la piattaforma Awareness li rinomina o ne aggiunge.
 */
const LEVEL_PALETTE = [
  { badge: 'bg-emerald-100 text-emerald-800', bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
  { badge: 'bg-sky-100 text-sky-800', bar: 'bg-sky-500', dot: 'bg-sky-500' },
  { badge: 'bg-amber-100 text-amber-800', bar: 'bg-amber-500', dot: 'bg-amber-500' },
  { badge: 'bg-red-100 text-red-800', bar: 'bg-red-500', dot: 'bg-red-500' },
];
const LEVEL_FALLBACK = { badge: 'bg-gray-100 text-gray-700', bar: 'bg-gray-400', dot: 'bg-gray-400' };

export function levelStyle(levelKey, levels) {
  if (!levelKey) return LEVEL_FALLBACK;
  const ordered = sortedLevels(levels);
  const rank = ordered.findIndex((l) => l.key === levelKey);
  if (rank === -1) return LEVEL_FALLBACK;
  if (ordered.length <= LEVEL_PALETTE.length) {
    // Allinea il livello peggiore all'ultimo colore della palette.
    const offset = LEVEL_PALETTE.length - ordered.length;
    return LEVEL_PALETTE[rank + offset] || LEVEL_FALLBACK;
  }
  return LEVEL_PALETTE[Math.min(rank, LEVEL_PALETTE.length - 1)] || LEVEL_FALLBACK;
}

/** Chiave del livello corrispondente a un indice, secondo le soglie del payload. */
export function levelForIndex(index, levels) {
  if (isMissing(index) || !Number.isFinite(Number(index))) return null;
  const ordered = sortedLevels(levels);
  const match = ordered.find((l) => Number(index) >= l.threshold);
  return match ? match.key : (ordered[ordered.length - 1]?.key ?? null);
}

/** Reparti ordinati dal meno consapevole. I valori nulli finiscono in coda. */
export function sortByAwareness(departments) {
  return [...(departments || [])].sort((a, b) => {
    const ai = a?.awareness_index;
    const bi = b?.awareness_index;
    if (isMissing(ai) && isMissing(bi)) return 0;
    if (isMissing(ai)) return 1;
    if (isMissing(bi)) return -1;
    return Number(ai) - Number(bi);
  });
}

/**
 * Target iniziale suggerito per un reparto, a partire dal suo `risk_weight`.
 *
 * È solo un punto di partenza modificabile: il peso di rischio è relativo agli
 * altri reparti dell'azienda, quindi la soglia proposta è quella del livello
 * corrispondente alla posizione del reparto rispetto al rischio massimo.
 */
export function suggestTarget(riskWeight, maxRiskWeight, levels) {
  const ordered = sortedLevels(levels).filter((l) => l.threshold > 0);
  if (ordered.length === 0) return null;

  const risk = Number(riskWeight);
  const max = Number(maxRiskWeight);
  if (!Number.isFinite(risk) || !Number.isFinite(max) || max <= 0) {
    // Senza peso di rischio utilizzabile proponiamo il livello intermedio.
    return ordered[Math.min(1, ordered.length - 1)].threshold;
  }

  const ratio = Math.min(1, risk / max);
  if (ratio >= 0.85) return ordered[0].threshold;
  if (ratio >= 0.6) return ordered[Math.min(1, ordered.length - 1)].threshold;
  return ordered[Math.min(2, ordered.length - 1)].threshold;
}

/** Peso di rischio massimo fra i reparti, base per il target suggerito. */
export function maxRiskWeight(departments) {
  const weights = (departments || [])
    .map((d) => Number(d?.risk_weight))
    .filter((w) => Number.isFinite(w) && w > 0);
  return weights.length ? Math.max(...weights) : 1;
}

/**
 * Un reparto è fuori target solo se abbiamo sia il target sia l'indice.
 * Senza dati non inventiamo un esito.
 */
export function isOutOfTarget(department, target) {
  if (isMissing(target) || isMissing(department?.awareness_index)) return false;
  return Number(department.awareness_index) < Number(target);
}

/**
 * Porta la heatmap in una forma canonica.
 *
 * I "corsi" sono in realtà i moduli di un unico percorso di awareness: la
 * piattaforma è passata da `courses`/`course_id`/`course_title` a
 * `modules`/`module_id`/`module_title`. Qui accettiamo entrambe le forme, così
 * i componenti restano ignari del nome dei campi remoti.
 */
export function normalizeHeatmap(heatmap) {
  if (!heatmap) return null;

  const toModules = (node) =>
    (node?.modules || node?.courses || []).map((m) => ({
      id: m.module_id ?? m.course_id,
      title: m.module_title ?? m.course_title,
      completedUsers: m.completed_users,
      completionRate: m.completion_rate,
    }));

  return {
    modules: toModules(heatmap),
    departments: (heatmap.departments || []).map((dept) => ({
      department_id: dept.department_id,
      department_name: dept.department_name,
      users_count: dept.users_count,
      small_sample: dept.small_sample,
      modules: toModules(dept),
    })),
  };
}

/** Colore della cella heatmap: la scala è sul completamento, non sull'indice. */
export function heatmapCellStyle(completionRate) {
  if (isMissing(completionRate) || !Number.isFinite(Number(completionRate))) {
    return 'bg-gray-100 text-gray-400';
  }
  const rate = Number(completionRate);
  if (rate >= 80) return 'bg-emerald-500 text-white';
  if (rate >= 60) return 'bg-emerald-300 text-emerald-900';
  if (rate >= 40) return 'bg-amber-300 text-amber-900';
  if (rate >= 20) return 'bg-orange-300 text-orange-900';
  if (rate > 0) return 'bg-red-300 text-red-900';
  return 'bg-red-100 text-red-700';
}
